import parse from 'html-react-parser'
import { isMetricLinkValue } from 'models/metric'
import { useStoTrack } from 'hooks/sto/useStoTracking'
import { CardTabs, TabCard, TabPanel } from 'components/sto/Tabs'
import { useCallback, useId, useState, type MouseEvent } from 'react'
import { SectionHeading } from 'components/sto/Section/Section.styles'
import { ArrowSimple, Copy, Tooltip, t } from '@stokr/components-library'
import { CompactButton, InfoIcon, Section } from 'components/sto/Section'
import CrossIcon from '@stokr/components-library/dist/images/cross-icon.svg?react'
import { DrawerBody, DrawerHeader, RoundButton, StoSideModal } from 'components/sto/Documents/Documents.styles'
import { formatDividendDate, useDividends, useHasDividends, type DividendHistory } from 'hooks/sto/useDividends'
import {
  Row,
  Body,
  Rows,
  Stat,
  Stats,
  Title,
  Toggle,
  Header,
  Details,
  Heading,
  NextBox,
  Subtitle,
  TitleRow,
  NextLine,
  StatValue,
  RowAction,
  StatLabel,
  NextTitle,
  DetailsInner,
  DeclarationAction,
  type NextDividendPlacement,
} from './Dividends.styles'

/** Rows shown in the section; the rest open in the drawer. */
const PREVIEW_ROWS = 5

const TAB_ANCHOR = 'dividends-history'

type Placement = 'section' | 'drawer'

/** Columns on a closed row's first line, desktop. Mobile shows one fewer; see `mobileOnly`. */
const FIRST_LINE = 3

const DividendStat = ({
  label,
  value,
  only,
}: {
  label: string
  value: string | undefined
  only?: 'desktop' | 'mobile'
}) => (
  <Stat data-only={only}>
    <StatLabel>{label}</StatLabel>
    <StatValue>{formatDividendDate(value?.trim() || '-')}</StatValue>
  </Stat>
)

/**
 * One distribution: the first line, then the rest sliding open. The third column closes the
 * first line on desktop and opens the details on mobile, so it is rendered in both places and
 * CSS shows one: the server render is the same for every screen.
 */
const DividendRow = ({
  history,
  row,
  placement,
}: {
  history: DividendHistory
  row: Record<string, string>
  placement: Placement
}) => {
  const [open, setOpen] = useState(false)
  const detailsId = useId()
  const trackSto = useStoTrack()
  const toggle = () => setOpen((value) => !value)
  const link = history.linkColumn && row[history.linkColumn.key]?.trim()
  const declaration = link && isMetricLinkValue(link) ? link : undefined
  const firstLine = history.columns.slice(0, FIRST_LINE)
  const shared = history.columns[FIRST_LINE - 1]
  const rest = history.columns.slice(FIRST_LINE)

  return (
    <Row data-open={open} onClick={toggle} data-cy="sto-dividends-row">
      <Stats>
        {firstLine.map((column) => (
          <DividendStat
            key={column.key}
            label={column.label}
            value={row[column.key]}
            only={column === shared ? 'desktop' : undefined}
          />
        ))}
      </Stats>
      <Details>
        <DetailsInner id={detailsId} inert={!open}>
          <Stats>
            {shared && <DividendStat label={shared.label} value={row[shared.key]} only="mobile" />}
            {rest.map((column) => (
              <DividendStat key={column.key} label={column.label} value={row[column.key]} />
            ))}
          </Stats>
          {declaration && (
            <RowAction>
              <CompactButton
                as="a"
                href={declaration}
                target="_blank"
                rel="noopener noreferrer"
                black
                data-cy="sto-dividends-view-declaration"
                onClick={(event: MouseEvent) => {
                  event.stopPropagation()
                  trackSto('sto_dividend_declaration_clicked', { location: placement })
                }}>
                <Copy id="stoPage.dividends.viewDeclaration" />
                <ArrowSimple direction="right" width={10} height={6} aria-hidden="true" />
              </CompactButton>
            </RowAction>
          )}
        </DetailsInner>
      </Details>
      <Toggle
        type="button"
        aria-expanded={open}
        aria-controls={detailsId}
        aria-label={t(open ? 'stoPage.dividends.hideDetails' : 'stoPage.dividends.showDetails')}
        onClick={(event) => {
          event.stopPropagation()
          toggle()
        }}>
        <ArrowSimple direction="bottom" width={12} height={8} aria-hidden="true" />
      </Toggle>
    </Row>
  )
}

const DividendRows = ({
  history,
  rows,
  placement,
}: {
  history: DividendHistory
  rows: Record<string, string>[]
  placement: Placement
}) => (
  <Rows>
    {rows.map((row, index) => (
      <DividendRow key={index} history={history} row={row} placement={placement} />
    ))}
  </Rows>
)

/** The author's title (with its tooltip) and subtitle, e.g. "Declaration · Record date 31 Aug 2026". */
const HistoryHeading = ({ history }: { history: DividendHistory }) => (
  <Heading>
    {history.title && (
      <TitleRow>
        <Title>{history.title}</Title>
        {history.tooltip && (
          <Tooltip placement="top" content={history.tooltip}>
            <InfoIcon role="button" tabIndex={0} aria-label={t('stoPage.dividends.moreInfo')} />
          </Tooltip>
        )}
      </TitleRow>
    )}
    {history.subtitle && <Subtitle>{parse(history.subtitle)}</Subtitle>}
  </Heading>
)

/** Every row in a side drawer, as Documents' full list. */
const FullHistoryDrawer = ({
  history,
  open,
  onClose,
}: {
  history: DividendHistory
  open: boolean
  onClose: () => void
}) => {
  const titleId = useId()
  return (
    <StoSideModal
      isOpen={open}
      onClose={onClose}
      showCloseButton={false}
      width={0}
      minWidth={704}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}>
      <DrawerBody>
        <DrawerHeader>
          <SectionHeading id={titleId}>
            <Copy id="stoPage.dividends.tab" />
          </SectionHeading>
          <RoundButton
            $size={40}
            type="button"
            aria-label={t('stoPage.dividends.closeList')}
            data-cy="sto-dividends-drawer-close"
            onClick={onClose}>
            <CrossIcon width={12} height={12} aria-hidden="true" />
          </RoundButton>
        </DrawerHeader>
        <DividendRows history={history} rows={history.rows} placement="drawer" />
      </DrawerBody>
    </StoSideModal>
  )
}

/** Figma 5174:32756 (desktop) / 5207:30414 (mobile): one tab of distributions, newest first as back-office orders them. */
const DividendDistributions = ({ history }: { history: DividendHistory }) => {
  const panelId = useId()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])
  const trackSto = useStoTrack()
  const hasMore = history.rows.length > PREVIEW_ROWS

  return (
    <TabCard>
      <CardTabs
        tabs={[{ id: 'history', label: t('stoPage.dividends.tab'), domId: TAB_ANCHOR }]}
        activeId="history"
        onSelect={() => {}}
        panelId={panelId}
        label={t('stoPage.dividends.label')}
        fit
        dataCy="sto-dividends-tab"
      />

      <TabPanel activeKey="history" id={panelId} aria-labelledby={TAB_ANCHOR}>
        <Body>
          <Header>
            <HistoryHeading history={history} />
            {hasMore && (
              <DeclarationAction
                type="button"
                black
                data-cy="sto-dividends-view-all"
                onClick={() => {
                  trackSto('sto_dividends_view_all_clicked', { count: history.rows.length })
                  setDrawerOpen(true)
                }}
                aria-haspopup="dialog">
                <Copy id="stoPage.dividends.viewFullHistory" />
                <ArrowSimple direction="right" width={10} height={6} aria-hidden="true" />
              </DeclarationAction>
            )}
          </Header>
          <DividendRows history={history} rows={history.rows.slice(0, PREVIEW_ROWS)} placement="section" />
          {hasMore && <FullHistoryDrawer history={history} open={drawerOpen} onClose={closeDrawer} />}
        </Body>
      </TabPanel>
    </TabCard>
  )
}

export const Dividends = () => {
  const state = useDividends()

  return (
    <Section anchor="dividends" state={state} variant="plain">
      {(history) => <DividendDistributions history={history} />}
    </Section>
  )
}

/** The small blue box with the dividend frequency; opens the section. */
export const NextDividend = ({ placement }: { placement: NextDividendPlacement }) => {
  const hasDividends = useHasDividends()
  const trackSto = useStoTrack()
  if (!hasDividends) return null

  return (
    <NextBox
      to={{ hash: 'dividends' }}
      $placement={placement}
      data-cy={`sto-next-dividend-${placement}`}
      onClick={() => trackSto('sto_next_dividend_clicked', { placement })}>
      <NextTitle>
        <Copy id="stoPage.dividends.nextTitle" />
      </NextTitle>
      <NextLine>
        <Copy id="stoPage.dividends.schedule" />
      </NextLine>
    </NextBox>
  )
}
